import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_API = `${BASE}/api/admin`;
const TOKEN_KEY = "asai_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const http = axios.create({ baseURL: ADMIN_API, timeout: 25000 });
http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      if (!window.location.pathname.startsWith("/admin/login")) {
        window.location.href = "/admin/login?expired=1";
      }
    }
    return Promise.reject(err);
  }
);

const msg = (err, fallback) => err?.response?.data?.message || err?.response?.data?.detail || fallback;

export const adminLogin = async (email, password) => {
  try {
    const { data } = await axios.post(`${ADMIN_API}/auth/login`, { email, password });
    setToken(data.token);
    return data.user;
  } catch (err) {
    throw new Error(msg(err, "Login failed. Please try again."));
  }
};

export const adminMe = async () => {
  const { data } = await http.get("/auth/me");
  return data.user;
};

export const adminLogout = async () => {
  try { await http.post("/auth/logout"); } catch (e) { /* ignore */ }
  clearToken();
};

export const getStats = async () => (await http.get("/stats")).data;

export const listData = async (resource, params) => (await http.get(`/data/${resource}`, { params })).data;

export const updateData = async (resource, id, body) => {
  try {
    return (await http.patch(`/data/${resource}/${id}`, body)).data;
  } catch (err) {
    throw new Error(msg(err, "Update failed."));
  }
};

export const listPrograms = async () => (await http.get("/programs")).data.items;
export const createProgram = async (body) => {
  try { return (await http.post("/programs", body)).data; }
  catch (err) { throw new Error(msg(err, "Could not create program.")); }
};
export const updateProgram = async (id, body) => {
  try { return (await http.patch(`/programs/${id}`, body)).data; }
  catch (err) { throw new Error(msg(err, "Could not update program.")); }
};
export const deleteProgram = async (id) => (await http.delete(`/programs/${id}`)).data;
export const reorderPrograms = async (orderedIds) => (await http.post("/programs/reorder", { orderedIds })).data;

export const getSettings = async () => (await http.get("/settings")).data.settings;
export const saveSettings = async (settings) => (await http.put("/settings", settings)).data;

// ----- students & content
export const listStudents = async (params) => (await http.get("/students", { params })).data;
export const enrollStudent = async (body) => {
  try { return (await http.post("/students", body)).data; }
  catch (e) { throw new Error(e?.response?.data?.message || "Could not enroll student."); }
};
export const updateStudent = async (id, body) => (await http.patch(`/students/${id}`, body)).data;
export const addStudentContent = async (body) => {
  try { return (await http.post("/student-content", body)).data; }
  catch (e) { throw new Error(e?.response?.data?.message || "Could not add content."); }
};
export const listStudentContent = async (studentId, type) => (await http.get("/student-content", { params: { studentId, type } })).data.items;
export const deleteStudentContent = async (id) => (await http.delete(`/student-content/${id}`)).data;
export const listAnnouncements = async () => (await http.get("/announcements")).data.items;
export const createAnnouncement = async (body) => {
  try { return (await http.post("/announcements", body)).data; }
  catch (e) { throw new Error(e?.response?.data?.message || "Could not post announcement."); }
};
export const deleteAnnouncement = async (id) => (await http.delete(`/announcements/${id}`)).data;
export const listTickets = async () => (await http.get("/support-tickets")).data.items;
export const updateTicket = async (id, status) => (await http.patch(`/support-tickets/${id}`, { status })).data;

// ----- content (gallery/blog/testimonials/events)
export const contentList = async (kind) => (await http.get(`/content/${kind}`)).data;
export const contentCreate = async (kind, body) => {
  try { return (await http.post(`/content/${kind}`, body)).data; }
  catch (e) { throw new Error(e?.response?.data?.message || "Could not save."); }
};
export const contentUpdate = async (kind, id, body) => (await http.patch(`/content/${kind}/${id}`, body)).data;
export const contentDelete = async (kind, id) => (await http.delete(`/content/${kind}/${id}`)).data;

// --- Courses (admin) ---
export const coursesList = async () => (await http.get(`/courses`)).data.items;
export const courseGet = async (id) => (await http.get(`/courses/${id}`)).data;
export const courseCreate = async (body) => (await http.post(`/courses`, body)).data;
export const courseUpdate = async (id, body) => (await http.patch(`/courses/${id}`, body)).data;
export const courseDelete = async (id) => (await http.delete(`/courses/${id}`)).data;
export const contentUpload = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const { data } = await http.post(`/content/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    });
    return data; // { url, path, originalName }
  } catch (e) {
    throw new Error(e?.response?.data?.detail || e?.response?.data?.message || "Upload failed.");
  }
};

// ----- chatbot FAQs
export const chatbotMeta = async () => (await http.get("/chatbot/meta")).data;
export const chatbotFaqList = async () => (await http.get("/chatbot/faqs")).data.items;
export const chatbotFaqCreate = async (body) => {
  try { return (await http.post("/chatbot/faqs", body)).data; }
  catch (e) {
    const d = e?.response?.data;
    throw { message: d?.message || "Could not save FAQ.", errors: d?.errors || {} };
  }
};
export const chatbotFaqUpdate = async (id, body) => {
  try { return (await http.patch(`/chatbot/faqs/${id}`, body)).data; }
  catch (e) {
    const d = e?.response?.data;
    throw { message: d?.message || "Could not update FAQ.", errors: d?.errors || {} };
  }
};
export const chatbotFaqDelete = async (id) => (await http.delete(`/chatbot/faqs/${id}`)).data;
export const chatbotLogs = async () => (await http.get("/chatbot/logs")).data.items;

// Authenticated file download (resume / export)
export const downloadFile = async (path, filename) => {
  const res = await http.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
